import XLSX from 'xlsx'

const REQUIRED_HEADERS = ['考核项目', '分项', '分值', '计算方式', '得分项', '评分部门']
const POINTS_FORMAT_HINT = '请填写整数（如 10）、范围（如 1~10）或百分比（如 (80%~100%)*20）。'

class ImportError extends Error {
    constructor(message) {
        super(message)
        this.name = 'ImportError'
        this.isImportError = true
    }
}

function hasValue(value) {
    return value !== undefined && value !== null && value !== ''
}

function text(value) {
    return hasValue(value) ? String(value).trim() : ''
}

function quoteList(values) {
    return values.map(value => `“${value}”`).join('、')
}

function formatLocation({sheetname, sheetIndex, row, subject, item, condition}) {
    const parts = []
    let location = ''

    if (sheetname) {
        location = `工作表“${sheetname}”${sheetIndex ? `（第${sheetIndex}个工作表）` : ''}`
    }
    if (row) {
        location += `第${row}行`
    }
    if (location) parts.push(location)
    if (subject !== undefined) parts.push(`考核项目${subject ? `“${subject}”` : '“未填写”'}`)
    if (item !== undefined) parts.push(`分项${item ? `“${item}”` : '“未填写”'}`)
    if (condition !== undefined) parts.push(`计算方式${condition ? `“${condition}”` : '“未填写”'}`)

    return parts.join('，')
}

function formatErrors(errors) {
    if (errors.length === 1) {
        return `导入失败：${errors[0]}请修改 Excel 后重新上传。`
    }

    const lines = errors.map((message, index) => `${index + 1}. ${message}`)

    return `导入失败，发现 ${errors.length} 处问题：\n${lines.join('\n')}\n请修改 Excel 后重新上传。`
}

function parsePoints(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? {points: value} : {error: true}
    }

    const raw = text(value)
    if (raw === '') return {error: true}
    if (/^-?\d+$/.test(raw)) return {points: parseInt(raw, 10)}

    const regex = /^(-?\d+)\s*~\s*(-?\d+)$|^\((-?\d+)%\s*~\s*(-?\d+)%\)\*(-?\d+)$/
    const found = raw.match(regex)

    if (!found) return {error: true}

    if (found[1] != null && found[2] != null) {
        return {
            points: parseInt(found[1], 10),
            min: parseInt(found[1], 10),
            max: parseInt(found[2], 10),
            isPercent: false
        }
    }

    if (found[3] != null && found[4] != null && found[5] != null) {
        return {
            points: parseInt(found[5], 10),
            min: parseInt(found[3], 10),
            max: parseInt(found[4], 10),
            isPercent: true
        }
    }

    return {error: true}
}

export default {
    name: "FileHelper",

    readfile({data, departmentid, departmentname, departmentsettings}) {
        let workbook

        try {
            workbook = XLSX.read(data, {type: 'array'})
        } catch (e) {
            throw new ImportError('导入失败：无法读取该文件，请确认它是有效的 Excel 文件（.xlsx）。')
        }

        try {
            if (!Array.isArray(departmentsettings) || departmentsettings.length === 0) {
                throw new ImportError('导入失败：没有读取到部门设置，请先在系统管理中配置部门，或刷新页面后重试。')
            }

            const errors = []
            const filedata = []

            workbook.SheetNames.forEach((sheetname, sheetIndex) => {
                if (sheetname.indexOf('Sheet') != -1) return

                const sheet = workbook.Sheets[sheetname]
                const rows = XLSX.utils.sheet_to_json(sheet, {header: 1, blankrows: false, defval: null})

                if (rows.length === 0) {
                    errors.push(`${formatLocation({sheetname, sheetIndex: sheetIndex + 1})}：没有数据。`)
                    return
                }

                const header = (rows[0] || []).map(cell => text(cell))
                const missingHeaders = REQUIRED_HEADERS.filter(name => header.indexOf(name) === -1)

                if (missingHeaders.length > 0) {
                    errors.push(`${formatLocation({sheetname, sheetIndex: sheetIndex + 1, row: 1})}：表头缺少${quoteList(missingHeaders)}。请使用标准模板表头。`)
                    return
                }

                if (rows.length < 2) {
                    errors.push(`${formatLocation({sheetname, sheetIndex: sheetIndex + 1})}：只有表头，没有数据行。`)
                    return
                }

                const entries = XLSX.utils.sheet_to_json(sheet)
                filedata.push({sheetname, sheetIndex: sheetIndex + 1, entries})
            })

            if (filedata.length === 0) {
                if (errors.length > 0) throw new ImportError(formatErrors(errors))
                throw new ImportError('导入失败：工作簿中没有可导入的工作表。工作表名称不能包含“Sheet”，且每个工作表应对应一名员工。')
            }

            const importeddata = this.parseimported({
                filedata,
                departmentid,
                departmentname,
                departmentsettings,
                errors
            })

            const result = this.fillDepartmentAndUserIds({importeddata, departmentsettings, errors})

            if (errors.length > 0) throw new ImportError(formatErrors(errors))

            return result
        } catch (e) {
            if (e && e.isImportError) throw e

            console.error('Excel import error:', e)
            throw new ImportError('导入失败：处理 Excel 时发生错误，请确认文件格式正确；如果问题持续，请联系管理员。')
        }
    },

    parseimported({filedata, departmentid, departmentname, departmentsettings, errors}) {
        const goals = []
        const configuredDepartments = new Set(
            (Array.isArray(departmentsettings) ? departmentsettings : [])
                .map(ds => text(ds.departmentname))
                .filter(Boolean)
        )

        filedata.forEach(data => {
            const {sheetname: username, sheetIndex, entries} = data
            const sheetErrorCount = errors.length

            const usergoal = {username, departmentid, departmentname, objectives: []}
            goals.push(usergoal)

            let subject = ''
            let item = ''
            let total = ''
            let ratedepartmentname = ''
            const entries1 = []
            const checkedItems = new Set()

            entries.forEach((entry, index) => {
                const excelRow = Number.isInteger(entry.__rowNum__) ? entry.__rowNum__ + 1 : index + 2

                const subjectBefore = subject
                const rawSubject = text(entry['考核项目'])
                const subjectCellPresent = rawSubject !== ''
                const subjectValueChanged = subjectCellPresent && rawSubject !== subjectBefore
                if (subjectCellPresent) subject = rawSubject

                if (subject.indexOf('合计') != -1) return

                const rawItem = text(entry['分项'])
                const rawRatedepartment = text(entry['评分部门'])
                const ratingDepartmentChanged = rawRatedepartment !== '' && rawRatedepartment !== ratedepartmentname
                const hasTotalCell = hasValue(entry['分值'])

                if (rawItem !== '') {
                    item = rawItem
                } else if (subjectValueChanged || (subjectCellPresent && (hasTotalCell || ratingDepartmentChanged))) {
                    item = ''
                }

                const condition = text(entry['计算方式']).replace(/^\s*\d+\s*[.、)．]\s*/, '')
                const rawPoints = entry['得分项']
                const pointsText = text(rawPoints)

                if (hasTotalCell) {
                    const totalNumber = Number(entry['分值'])
                    if (Number.isFinite(totalNumber)) {
                        total = totalNumber
                    } else {
                        errors.push(`${formatLocation({sheetname: username, sheetIndex, row: excelRow, subject, item, condition})}：“分值”的值“${entry['分值']}”不是有效数字，请填写数字。`)
                    }
                }

                if (rawRatedepartment !== '') {
                    ratedepartmentname = rawRatedepartment
                }

                const missing = []
                if (subject === '') missing.push('考核项目')
                if (item === '') missing.push('分项')
                if (condition === '') missing.push('计算方式')
                if (pointsText === '') missing.push('得分项')
                if (ratedepartmentname === '') missing.push('评分部门')

                const parsedPoints = pointsText === '' ? null : parsePoints(rawPoints)
                const pointsFormatError = parsedPoints !== null && parsedPoints.error
                const pointsRangeError = parsedPoints !== null && !parsedPoints.error
                    && parsedPoints.min !== undefined && parsedPoints.min > parsedPoints.max

                if (missing.length > 0) {
                    errors.push(`${formatLocation({sheetname: username, sheetIndex, row: excelRow, subject, item, condition})}：缺少${quoteList(missing)}。`)
                }

                if (pointsFormatError) {
                    errors.push(`${formatLocation({sheetname: username, sheetIndex, row: excelRow, subject, item, condition})}：“得分项”的值“${rawPoints}”格式不正确。${POINTS_FORMAT_HINT}`)
                }

                if (pointsRangeError) {
                    errors.push(`${formatLocation({sheetname: username, sheetIndex, row: excelRow, subject, item, condition})}：“得分项”范围“${rawPoints}”不正确，最小值不能大于最大值。`)
                }

                if (missing.length > 0 || pointsFormatError || pointsRangeError) return

                const ratedepartments = ratedepartmentname
                    .split(/\s*(?:[、|，|,|;|；])\s*/)
                    .map(name => name.trim())
                    .filter(Boolean)
                    .map(name => ({name}))

                const itemKey = `${subject}\u0000${item}`

                if (!checkedItems.has(itemKey)) {
                    checkedItems.add(itemKey)

                    const unknownDepartments = ratedepartments
                        .map(department => department.name)
                        .filter(name => !configuredDepartments.has(name))

                    if (unknownDepartments.length > 0) {
                        errors.push(`${formatLocation({
                            sheetname: username,
                            sheetIndex,
                            row: excelRow,
                            subject,
                            item,
                            condition
                        })}：评分部门${quoteList(unknownDepartments)}在系统管理中不存在。请先在系统管理中添加该部门，或修改 Excel 中的部门名称。`)
                    }
                }

                entries1.push({
                    subject,
                    item,
                    total,
                    condition,
                    points: parsedPoints,
                    ratedepartments
                })
            })

            entries1.forEach(entry => {
                let objective = usergoal.objectives.find(o => o.name === entry.subject)
                if (!objective) {
                    objective = {name: entry.subject, items: []}
                    usergoal.objectives.push(objective)
                }

                let itemObj = objective.items.find(it => it.name === entry.item)
                if (!itemObj) {
                    itemObj = {
                        name: entry.item,
                        total: entry.total,
                        ratedepartments: entry.ratedepartments,
                        creteria: []
                    }
                    objective.items.push(itemObj)
                }

                itemObj.creteria.push({
                    name: entry.condition,
                    points: entry.points.points,
                    min: entry.points.min,
                    max: entry.points.max,
                    isPercent: entry.points.isPercent
                })
            })

            if (usergoal.objectives.length === 0 && errors.length === sheetErrorCount) {
                errors.push(`${formatLocation({sheetname: username, sheetIndex})}：没有可导入的考核目标（可能只有“合计”行）。`)
            }
        })

        return goals
    },

    fillDepartmentAndUserIds({importeddata, departmentsettings, errors}) {
        const userobjs = importeddata

        if (!Array.isArray(departmentsettings)) {
            errors.push('未读取到部门设置，请刷新页面后重试。')
            return userobjs
        }

        const matchedUsers = new Map()

        this.walkthroughItems(userobjs, {
            itemcb: ({item}) => {
                if (!Array.isArray(item.ratedepartments)) return

                const splittedDeparments = []

                item.ratedepartments.forEach(department => {
                    if (department.id) {
                        splittedDeparments.push(department)
                        return
                    }

                    const d0 = departmentsettings.find(ds => text(ds.departmentname) === text(department.name))
                    if (d0) {
                        splittedDeparments.push({id: d0.departmentid, name: d0.departmentname})
                    }
                    // parseimported already reports unknown departments with row context.
                })

                item.ratedepartments = splittedDeparments
            },
            userobjectivescb: ({userobjectives}) => {
                const sheetname = userobjectives.username
                const d0 = departmentsettings.find(ds => ds.departmentid === userobjectives.departmentid)

                if (!d0 || !Array.isArray(d0.departmentmembers)) {
                    errors.push(`工作表“${sheetname}”：未找到部门“${userobjectives.departmentname}”的设置，请刷新页面后重试。`)
                    return
                }

                const matches = d0.departmentmembers.filter(member => sheetname.indexOf(member.name) !== -1)

                if (matches.length === 0) {
                    errors.push(`工作表“${sheetname}”：未匹配到部门“${userobjectives.departmentname}”的成员。请确认工作表名称包含员工姓名，或先在系统管理中把该员工加入部门成员。`)
                    return
                }

                if (matches.length > 1) {
                    errors.push(`工作表“${sheetname}”：匹配到多个部门成员${quoteList(matches.map(member => member.name))}。请将工作表名称改为完整姓名，或调整部门成员。`)
                    return
                }

                const member = matches[0]

                if (matchedUsers.has(member.userid)) {
                    errors.push(`工作表“${matchedUsers.get(member.userid)}”和“${sheetname}”都匹配到员工“${member.name}”，请只保留一个工作表。`)
                    return
                }

                matchedUsers.set(member.userid, sheetname)
                userobjectives.userid = member.userid
                userobjectives.username = member.name
            }
        })

        return userobjs
    },

    walkthroughItems(userobjs, {userobjectivescb, objectivecb, itemcb, creteriacb}) {

        // console.log(`walkthroughItems`)
        userobjs.forEach( userobjectives=>{
          if (userobjectivescb) userobjectivescb({userobjectives})
          // console.log(`walk userobjectives`)
          userobjectives.objectives.forEach(objective=>{
              if (objectivecb) objectivecb({objective, userobjectives})
              // console.log(`walk objective`)
              objective.items.forEach(item=>{
                  // console.log(`walk item`)
                  if (itemcb) itemcb({item, objective, userobjectives})

                  if (item.creteria && creteriacb) {
                    item.creteria.forEach(cr => creteriacb({'creteria':cr, item, objective, userobjectives}))
                  }
              })
          })
        })
    }
}
